import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Badge, { BadgeType } from '../components/ui/Badge/Badge';
import Divider from '../components/ui/Divider/Divider';
import ContainerBody from '../components/ui/ContainerOverlay/ContainerBody/ContainerBody';
import Overlay from '../components/ui/Overlay/Overlay';
import OverlayHead from '../components/ui/Overlay/OverlayHead';
import { OverlayContext } from '../providers/OverlayProvider';
import { CommitmentStatus, COMMITMENT_STATUS_LABEL_KEY, useCommitmentQuery } from '../services/commitment';

const STATUS_BADGE: Record<string, BadgeType> = {
  pending_customer: BadgeType.Warning,
  active: BadgeType.Successful,
  rejected_customer: BadgeType.Error,
  terminated: BadgeType.Neutral,
  superseded: BadgeType.Neutral,
};

const fmtDate = (value?: string | null) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString('de-AT');
};

function Field({ label, value }: { label: string; value?: React.ReactNode }) {
  if (!value) return null;
  return (
    <div className="flex flex-col gap-4">
      <span className="typo-text-12--bold text-faded">{label}</span>
      <span className="typo-text-14 text-full whitespace-pre-line">{value}</span>
    </div>
  );
}

function CommitmentDetail({ id }: { id: number }) {
  const { t } = useTranslation();
  const { data: commitment } = useCommitmentQuery(id);
  const label = (key: string, fallback: string) => t(key, { defaultValue: fallback });
  if (!commitment) return null;
  const c = commitment as any;
  const statusLabel = t(COMMITMENT_STATUS_LABEL_KEY[c.status as CommitmentStatus] ?? `commitments.status_${c.status}`, {
    defaultValue: c.status,
  });

  return (
    <div className="flex flex-col gap-y-24 max-w-[720px]">
      <div className="flex items-start justify-between gap-16">
        <div className="flex flex-col gap-4">
          <span className="typo-text-12 text-faded">{c.name}</span>
          <h2 className="typo-title-36 text-full">{c.title}</h2>
          <span className="typo-text-14 text-faded">{c.company_name}</span>
        </div>
        <Badge className="max-w-min" label={statusLabel} expanded type={STATUS_BADGE[c.status] ?? BadgeType.Neutral} />
      </div>

      <Divider />

      <Field label={label('commitments.field_content', 'Content')} value={c.content} />
      <Field label={label('commitments.field_condition', 'Condition')} value={c.condition} />

      <div className="grid grid-cols-2 gap-16">
        <Field
          label={label('commitments.col_category', 'Category')}
          value={c.category ? t(`commitments.category.${c.category}`, { defaultValue: c.category }) : null}
        />
        <Field
          label={label('commitments.field_source', 'Source')}
          value={c.source_type ? t(`commitments.source.${c.source_type}`, { defaultValue: c.source_type }) : null}
        />
        <Field label={label('commitments.field_source_date', 'Source date')} value={fmtDate(c.source_date)} />
        <Field label={label('commitments.col_date_from', 'Valid from')} value={fmtDate(c.date_from)} />
        <Field label={label('commitments.col_date_to', 'Valid until')} value={fmtDate(c.date_to)} />
      </div>

      <Field label={label('commitments.field_source_note', 'Source note')} value={c.source_note} />
      <Field
        label={label('commitments.field_rejection_reason', 'Decline reason')}
        value={c.customer_rejection_reason}
      />
    </div>
  );
}

// State-driven slide-in overlay (the Agreements list lives inside the Settings
// tabs, so the detail is opened from local state rather than a nested route).
export function CommitmentDetailOverlay({ id, onClose }: { id: number; onClose: () => void }) {
  const { t } = useTranslation();
  const value = useMemo(() => ({ onClose, actions: null, setActions: () => {} }), [onClose]);

  return (
    <OverlayContext.Provider value={value}>
      <Overlay>
        <OverlayHead title={t('commitments.detail_title', { defaultValue: 'Agreement' })} />
        <ContainerBody>
          <CommitmentDetail id={id} />
        </ContainerBody>
      </Overlay>
    </OverlayContext.Provider>
  );
}

export default CommitmentDetailOverlay;
